import { config } from "@fortawesome/fontawesome-svg-core";
import DinesSpinner from "@/components/library/Misc/DinesSpinner";
import RouteLoader from "@/components/library/Misc/RouteLoader";
import { LoadingContextProvider } from "@/context/LoadingContext";
import { SessionProvider } from "next-auth/react";
import MainLayout from "@/layouts/MainLayout";
import Scripts from "@/layouts/scripts";
import "@/styles/globals.css";
import "@/fonts/fonts.css";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "@fortawesome/fontawesome-svg-core/styles.css";
import "react-toastify/dist/ReactToastify.css";
import type { AppContext, AppInitialProps, AppProps } from "next/app";
import type { WithRouterProps } from "next/dist/client/with-router";
import type { Session } from "next-auth";
import type { NextComponentType } from "next";
import { MenuProvider } from "@/context/Menu";
import { APIProvider } from "@/context/API";
import { type IOrganisationInfo, VenueSpecificProvider } from "@/context/Venue";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { SaveBarProvider } from "@/context/Savebar";
import Savebar from "@/components/library/Misc/Savebar";
import { ToastContainer } from "react-toastify";
import { MessagingProvider } from "@/context/Messaging";
import { NuqsAdapter } from "nuqs/adapters/next/pages";

config.autoAddCss = false;

interface OrgDashboardAppProps extends WithRouterProps {
  session: Session;
}

const queryClient = new QueryClient();

const LoggedInApp = ({ Component, pageProps }) => {
  return (
    <SaveBarProvider>
      <Savebar />
      <MenuProvider {...pageProps}>
        <MainLayout {...pageProps}>
          <Component {...pageProps} />
        </MainLayout>
      </MenuProvider>
      <ToastContainer />
    </SaveBarProvider>
  );
};

const LoggedOutApp = ({ Component, pageProps }) => {
  return <Component {...pageProps} />;
};

const App: NextComponentType<
  AppContext,
  AppInitialProps,
  AppProps & OrgDashboardAppProps
> = ({ Component, pageProps }) => {
  const { session } = pageProps;

  const organisationInfo: IOrganisationInfo = {
    address1: pageProps.organisation?.address1,
    town: pageProps.organisation?.town,
    postcode: pageProps.organisation?.postcode,
    contact_email: pageProps.organisation?.contact_email,
    contact_number: pageProps.organisation?.contact_number,
    account_name: pageProps.organisation?.account_name,
    telephone: pageProps.organisation?.telephone,
    country_code: pageProps.organisation?.country_code,
    companies_house_number: pageProps.organisation?.companies_house_number,
    vat_number: pageProps.organisation?.vat_number,
    pin_protected_permissions:
      pageProps.organisation?.pin_protected_permissions,
    business_type: pageProps.organisation?.business_type,
  };

  return (
    <>
      <Scripts />
      <SessionProvider session={session}>
        <VenueSpecificProvider
          lastSelectedVenue={pageProps.lastSelectedVenue}
          isVenueSpecific={pageProps.isVenueSpecific}
          organisationRelationships={pageProps.organisationRelationships}
          timezone={pageProps.organisation?.timezone}
          currency={pageProps.organisation?.assigned_currency}
          organisation_id={pageProps.organisation?.id}
          organisation_identifier={pageProps.organisation?.identifier}
          organisation_info={organisationInfo}
          featureFlags={pageProps.featureFlags}
        >
          <QueryClientProvider client={queryClient}>
            <NuqsAdapter>
              <APIProvider>
                <LoadingContextProvider>
                  <RouteLoader />
                  <DinesSpinner />
                  <ReactQueryDevtools initialIsOpen={false} />
                  {session ? (
                    <MessagingProvider restaurantId={session.restaurant}>
                      <LoggedInApp
                        Component={Component}
                        pageProps={pageProps}
                      />
                    </MessagingProvider>
                  ) : (
                    <LoggedOutApp Component={Component} pageProps={pageProps} />
                  )}
                </LoadingContextProvider>
              </APIProvider>
            </NuqsAdapter>
          </QueryClientProvider>
        </VenueSpecificProvider>
      </SessionProvider>
    </>
  );
};

export default App;
